import React, { useState, useEffect, useRef } from 'react';
import { Search, Bell, ChevronDown, X, User, Building2, Receipt, Users, LogOut, Menu, AlertTriangle, Calendar, MessageSquare, Clock, Lightbulb, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Search suggestions for help
const SEARCH_SUGGESTIONS = [
  { text: 'Rechercher un immeuble', example: 'ex: "Résidence Soleil", "Paris"', icon: Building2 },
  { text: 'Rechercher un coproprietaire', example: 'ex: "Dupont", "Martin"', icon: User },
  { text: 'Rechercher un incident', example: 'ex: "fuite", "ascenseur"', icon: AlertTriangle },
  { text: 'Rechercher une assemblée', example: 'ex: "AG 2026", "budget"', icon: Calendar },
];

// Max history items to store
const MAX_HISTORY_ITEMS = 5;
const SEARCH_HISTORY_KEY = 'syndicappli_search_history';

const Header = ({ onMenuClick, showMenuButton }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHelp, setShowSearchHelp] = useState(false);
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Refs for click outside detection
  const userMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load search history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (saved) {
        setSearchHistory(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading search history:', e);
    }
  }, []);

  // Save search to history
  const saveToHistory = (query, result) => {
    if (!query || query.length < 2) return;
    
    const historyItem = {
      query,
      timestamp: Date.now(),
      resultType: result?.type || null,
      resultTitle: result?.title || null,
      url: result?.url || null
    };

    setSearchHistory(prev => {
      // Remove duplicates and add new item at the beginning
      const filtered = prev.filter(item => item.query.toLowerCase() !== query.toLowerCase());
      const newHistory = [historyItem, ...filtered].slice(0, MAX_HISTORY_ITEMS);
      
      // Save to localStorage
      try {
        localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(newHistory));
      } catch (e) {
        console.error('Error saving search history:', e);
      }
      
      return newHistory;
    });
  };

  // Clear search history
  const clearHistory = () => {
    setSearchHistory([]);
    try {
      localStorage.removeItem(SEARCH_HISTORY_KEY);
    } catch (e) {
      console.error('Error clearing search history:', e);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown if clicking outside
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      // Close notifications if clicking outside
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      // Close search results if clicking outside
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
        setShowSearchHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 2) {
      setSearchLoading(true);
      setShowSearchHelp(false);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else if (searchQuery.length === 0) {
      setSearchResults([]);
      setShowSearchResults(false);
    } else {
      // Show help when typing 1 character
      setShowSearchHelp(true);
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const performSearch = async (query) => {
    try {
      const response = await axios.get(`${API}/search`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.results || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchResultClick = (result) => {
    saveToHistory(searchQuery, result);
    setShowSearchResults(false);
    setShowSearchHelp(false);
    setSearchQuery('');
    navigate(result.url);
  };

  const handleHistoryItemClick = (item) => {
    if (item.url) {
      setShowSearchResults(false);
      setShowSearchHelp(false);
      setSearchQuery('');
      navigate(item.url);
    } else {
      setSearchQuery(item.query);
    }
  };

  const handleSearchFocus = () => {
    if (searchQuery.length >= 2) {
      setShowSearchResults(true);
    } else if (searchQuery.length === 1) {
      setShowSearchHelp(true);
    } else if (searchHistory.length > 0) {
      setShowSearchHelp(true);
    }
  };

  const getSearchIcon = (type) => {
    switch (type) {
      case 'building': return <Building2 size={16} className="text-blue-500" />;
      case 'user': return <User size={16} className="text-green-500" />;
      case 'incident': return <AlertTriangle size={16} className="text-orange-500" />;
      case 'assembly': return <Calendar size={16} className="text-purple-500" />;
      case 'message': return <MessageSquare size={16} className="text-teal-500" />;
      default: return <Search size={16} className="text-gray-400" />;
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotifications();
    }
  }, [token]);

  const fetchNotifications = async () => {
    try {
      setLoadingNotifs(true);
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both old format (array) and new format (object with notifications array)
      const data = response.data;
      if (Array.isArray(data)) {
        setNotifications(data);
      } else {
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoadingNotifs(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (notification) => {
    setShowNotifications(false);
    if (notification.type === 'message') {
      navigate('/messagerie');
    } else if (notification.type === 'incident') {
      navigate('/incidents');
    } else if (notification.type === 'assemblee') {
      navigate('/assemblees');
    }
  };

  const getInitials = () => {
    if (!user) return 'JD';
    return `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() || 'JD';
  };

  const getFullName = () => {
    if (!user) return 'John Doe';
    const fullName = `${user.prenom || ''} ${user.nom || ''}`.trim();
    return fullName || 'Utilisateur';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message': return '💬';
      case 'incident': return '🚨';
      case 'assemblee': return '📋';
      default: return '🔔';
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header className="bg-white border-b border-slate-100 px-4 md:px-6 h-16 flex items-center shadow-sm" data-testid="header">
      <div className="flex items-center justify-between w-full">
        {/* Mobile menu button */}
        {showMenuButton && (
          <button
            onClick={onMenuClick}
            className="p-2 mr-2 text-gray-600 hover:text-[#00D3A8] hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
        )}
        
        {/* Search Bar - Pill shaped like mockup */}
        <div className="flex-1 max-w-md hidden md:block" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={handleSearchFocus}
              className="w-full pl-11 pr-4 py-2.5 bg-[#F6F8FA] border-0 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#00D3A8]/30 placeholder:text-slate-400 transition-all"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  setShowSearchResults(false);
                  setShowSearchHelp(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={14} />
              </button>
            )}
            
            {/* Search Help & History Dropdown */}
            {showSearchHelp && !showSearchResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden">
                {/* Search History */}
                {searchHistory.length > 0 && searchQuery.length === 0 && (
                  <>
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                        <Clock size={12} />
                        Recherches récentes
                      </span>
                      <button
                        onClick={clearHistory}
                        className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                      >
                        Effacer
                      </button>
                    </div>
                    {searchHistory.map((item, index) => (
                      <button
                        key={`history-${index}`}
                        onClick={() => handleHistoryItemClick(item)}
                        className="w-full px-4 py-2.5 hover:bg-slate-50 transition-colors text-left flex items-center gap-3 border-b border-slate-50 last:border-0"
                      >
                        <Clock size={14} className="text-slate-300" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-slate-700">{item.query}</span>
                          {item.resultTitle && (
                            <span className="text-xs text-slate-400 ml-2">→ {item.resultTitle}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </>
                )}

                {/* Search Tips when typing */}
                {searchQuery.length >= 1 && searchQuery.length < 2 && (
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mb-3">
                      <Lightbulb size={16} />
                      <span className="text-xs">Tapez au moins 2 caractères pour rechercher</span>
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                  <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                    <TrendingUp size={12} />
                    Suggestions
                  </span>
                </div>
                {SEARCH_SUGGESTIONS.map((suggestion, index) => (
                  <div
                    key={`suggestion-${index}`}
                    className="px-4 py-2.5 text-left flex items-center gap-3 border-b border-slate-50 last:border-0"
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                      <suggestion.icon size={14} className="text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600">{suggestion.text}</p>
                      <p className="text-xs text-slate-400">{suggestion.example}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden max-h-96 overflow-y-auto">
                {searchLoading ? (
                  <div className="px-4 py-8 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00D3A8] border-t-transparent mx-auto"></div>
                    <p className="text-sm text-slate-400 mt-2">Recherche en cours...</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-400">
                    <Search size={24} className="mx-auto mb-2 text-slate-200" />
                    <p className="text-sm">Aucun résultat pour "{searchQuery}"</p>
                  </div>
                ) : (
                  <>
                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                      <span className="text-xs font-medium text-slate-500">
                        {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    {searchResults.map((result, index) => (
                      <button
                        key={`${result.type}-${result.id}-${index}`}
                        onClick={() => handleSearchResultClick(result)}
                        className="w-full px-4 py-3 hover:bg-slate-50 transition-colors text-left flex items-center gap-3 border-b border-slate-50 last:border-0"
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                          {getSearchIcon(result.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">
                            {result.title}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {result.subtitle}
                          </p>
                        </div>
                        <span className="text-[10px] text-slate-400 uppercase px-2 py-0.5 bg-slate-100 rounded">
                          {result.type === 'building' && 'Immeuble'}
                          {result.type === 'user' && 'Copropriétaire'}
                          {result.type === 'incident' && 'Incident'}
                          {result.type === 'assembly' && 'AG'}
                          {result.type === 'message' && 'Message'}
                        </span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-3 ml-6">
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button 
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowDropdown(false);
              }}
              className="relative p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-full transition-colors"
              data-testid="notifications-btn"
            >
              <Bell size={20} strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] font-medium flex items-center justify-center px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                  <span className="font-semibold text-slate-800 text-sm">Notifications</span>
                  <button 
                    onClick={() => setShowNotifications(false)}
                    className="text-slate-400 hover:text-slate-600 p-1 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {loadingNotifs ? (
                    <div className="px-4 py-8 text-center text-slate-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00D3A8] border-t-transparent mx-auto"></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-10 text-center text-slate-400">
                      <Bell size={28} className="mx-auto mb-3 text-slate-200" strokeWidth={1.5} />
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <button
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className="w-full px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-0"
                      >
                        <div className="flex items-start space-x-3">
                          <span className="text-lg">{getNotificationIcon(notif.type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {notif.title}
                            </p>
                            <p className="text-xs text-slate-400 truncate mt-0.5">
                              {notif.content}
                            </p>
                          </div>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-[#00D3A8] rounded-full flex-shrink-0 mt-1.5"></span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50">
                    <button 
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/notifications');
                      }}
                      className="text-[#00D3A8] text-xs font-medium hover:text-[#00C7A1] w-full text-center transition-colors"
                    >
                      Voir toutes les notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => {
                setShowDropdown(!showDropdown);
                setShowNotifications(false);
              }}
              className="flex items-center space-x-3 py-1.5 px-2 hover:bg-slate-50 rounded-full transition-colors"
              data-testid="user-menu-btn"
            >
              {/* Avatar cercle - violet clair comme mockup */}
              <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center bg-violet-100 text-violet-600 font-semibold text-sm">
                {user?.photo_url ? (
                  <img 
                    src={user.photo_url} 
                    alt="Photo de profil" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  getInitials()
                )}
              </div>
              <span className="font-medium text-slate-700 text-sm hidden sm:block">{getFullName()}</span>
              <ChevronDown size={14} className="text-slate-400" />
            </button>

            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden">
                <div className="p-2">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/mon-compte');
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                    data-testid="menu-mon-compte"
                  >
                    <User size={16} strokeWidth={1.5} />
                    <span className="text-sm">Mon compte</span>
                  </button>
                  
                  {user?.role === 'gestionnaire' && (
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/mon-cabinet');
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                    >
                      <Building2 size={16} strokeWidth={1.5} />
                      <span className="text-sm">Mon Cabinet</span>
                    </button>
                  )}
                  
                  {user?.role === 'coproprietaire' && (
                    <>
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          navigate('/mes-factures');
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                      >
                        <Receipt size={16} strokeWidth={1.5} />
                        <span className="text-sm">Mes Factures</span>
                      </button>
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          navigate('/sous-comptes');
                        }}
                        className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                      >
                        <Users size={16} strokeWidth={1.5} />
                        <span className="text-sm">Sous comptes</span>
                      </button>
                    </>
                  )}
                  
                  {/* Notifications settings link - available for all users */}
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/parametres');
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                    data-testid="menu-parametres"
                  >
                    <Bell size={16} strokeWidth={1.5} />
                    <span className="text-sm">Notifications</span>
                  </button>
                  
                  {/* Navigation Statistics link - available for all users */}
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      navigate('/mes-statistiques');
                    }}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 rounded-lg transition-colors flex items-center space-x-3 text-slate-600"
                    data-testid="menu-mes-statistiques"
                  >
                    <TrendingUp size={16} strokeWidth={1.5} />
                    <span className="text-sm">Mes Statistiques</span>
                  </button>
                </div>
                
                <div className="border-t border-slate-100 p-2">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2.5 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-3 text-red-600"
                    data-testid="logout-btn"
                  >
                    <LogOut size={16} strokeWidth={1.5} />
                    <span className="text-sm">Déconnexion</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
